import type {TFunction} from 'i18next';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {FlowPreview, PreviewFanOut} from 'crqlar-react-common/dist/models/automation/flowPreview.model';
import {eventFanOut, isSendStep, PreviewCount} from './flowPreviewModel';

/** The skip / stop codes the API sends, in words; anything else is the step's own message. */
export const reasonText = (reason: string | undefined, t: TFunction): string => {
    switch (reason) {
        case undefined:
            return '';
        case 'NO_EMAIL_ADDRESS':
            return t('No email address');
        case 'NO_RECIPIENT_CONFIGURED':
            return t('No recipient set');
        case 'NO_APP_ACCOUNT':
            return t('No guest app');
        case 'NO_PUSH_TOKEN':
            return t('Push notifications turned off');
        case 'PROFILE_NOT_FOUND':
            return t('Guest not found');
        case 'AI_DECISION':
            return t('Decided by AI when the flow runs');
        case 'NESTED_FAN_OUT':
            return t('Starts its own run for every member, which the preview does not follow');
        default:
            return t(reason);
    }
};

/** One line on what a card's number means ("25 continue · 2 stop here"), for its tooltip and the dialog. */
export const countMeaning = (
    nodeId: string,
    type: NodeType,
    count: PreviewCount,
    preview: FlowPreview,
    t: TFunction
): string => {
    if (count.kind === 'notReached') return t('Nobody gets this far right now');
    if (count.kind === 'notPreviewed') return t('Decided by AI when the flow runs');

    const node = preview.nodes.find(entry => entry.nodeId === nodeId);
    const problem = node?.problem ? reasonText(node.problem, t) : t('This step would fail');
    if (count.kind === 'failed') return problem;

    const outcomes = node?.outcomes ?? {};
    const fanOut = preview.fanOuts.find(entry => entry.nodeId === nodeId);
    const parts: string[] = [];

    if (fanOut) {
        // "Each time" is for a team below the trigger's events, never for the trigger itself.
        const perEvent = !!eventFanOut(preview) && fanOut.nodeId !== preview.triggerNodeId;
        parts.push(fanOutMeaning(fanOut, count.value, perEvent, t));
    } else if (type === NodeType.IF_CONDITION) {
        parts.push(t('Preview yes no', {yes: outcomes.YES ?? 0, no: outcomes.NO ?? 0}));
    } else if (isSendStep(type)) {
        parts.push(type === NodeType.SEND_PUSH
            ? t('Preview would get this push', {count: count.value})
            : t('Preview would get this email', {count: count.value}));
        if (outcomes.NOT_SENT) parts.push(t('Preview skipped', {count: outcomes.NOT_SENT}));
    } else if (type === NodeType.CONTINUE_IF) {
        parts.push(t('Preview continue', {count: count.value}));
        if (outcomes.STOPPED) parts.push(t('Preview stop here', {count: outcomes.STOPPED}));
    } else {
        parts.push(t('Preview go on', {count: count.value}));
    }

    if (count.failed) parts.push(problem);
    return parts.join(' · ');
};

const fanOutMeaning = (fanOut: PreviewFanOut, count: number, perEvent: boolean, t: TFunction): string => {
    switch (fanOut.memberType) {
        case 'SURVEY_INVITATION':
            return t('Preview reminders due', {count});
        case 'USER':
            return perEvent ? t('Preview people each time', {count}) : t('Preview people right now', {count});
        default:
            return perEvent ? t('Preview guests each time', {count}) : t('Preview guests right now', {count});
    }
};

/**
 * What the numbers are based on when that is not simply "now": the reminders due at the next check, or for a
 * manual trigger that reminds now (utils/remindNow) the reminders it would send right away.
 */
export const previewScope = (preview: FlowPreview, t: TFunction, triggerType?: NodeType): string | null =>
    eventFanOut(preview)?.memberType !== 'SURVEY_INVITATION' ? null
        : triggerType === NodeType.MANUAL_TRIGGER ? t('Preview scope remind now') : t('Preview scope due now');
